import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '@/context/AuthContext';
import { episodesAPI, configAPI } from '@/lib/api';
import Sidebar from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import useSEO from '@/hooks/useSEO';
import { 
  Upload, Clock, FileAudio, CheckCircle, AlertCircle, 
  Loader2, Trash2, Eye, Plus, Mic, Crown, CreditCard, AlertTriangle, RefreshCw, FileText, Zap
} from 'lucide-react';

// Subscription Status Banner Component
const SubscriptionBanner = ({ user }) => {
  if (!user) return null;
  
  const status = user.subscription_status?.toLowerCase() || '';
  const plan = user.plan || 'free';
  
  // Only show banner for non-free plans with issues
  if (plan === 'free') return null;
  
  // Past due - payment failed
  if (status === 'past_due' || user.payment_failed) {
    return (
      <motion.div 
        className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="subscription-past-due-banner"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="font-semibold text-red-400">Payment Failed</h3>
            <p className="text-sm text-neutral-400 mt-1">
              Your subscription payment could not be processed. Please update your payment method to continue using premium features.
            </p>
            <div className="flex gap-3 mt-3">
              <a 
                href="https://app.lemonsqueezy.com/my-orders" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium transition-colors"
              >
                <CreditCard className="w-4 h-4" />
                Update Payment Method
              </a>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }
  
  // Cancelled/expired
  if (status === 'cancelled' || status === 'expired') {
    // Check if cancelled but still in grace period
    const endDateStr = user.subscription_end_date;
    const inGracePeriod = status === 'cancelled' && endDateStr && new Date(endDateStr) > new Date();
    const formattedEnd = endDateStr ? new Date(endDateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : null;
    
    if (inGracePeriod) {
      // Cancelled but STILL ACTIVE — informative, not alarming
      return (
        <motion.div 
          className="mb-6 p-4 rounded-lg bg-blue-500/10 border border-blue-500/30"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="subscription-cancelled-grace-banner"
        >
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-300">
                Subscription Cancelled
              </h3>
              <p className="text-sm text-neutral-400 mt-1">
                Your <span className="text-white font-medium capitalize">{plan}</span> plan remains fully active until <span className="text-white font-medium">{formattedEnd}</span>. After that, your account will move to Free unless you resume.
              </p>
            </div>
          </div>
        </motion.div>
      );
    }
    
    // Fully expired / past grace period — this IS a downgrade
    return (
      <motion.div 
        className="mb-6 p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="subscription-expired-banner"
      >
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="font-semibold text-yellow-400">
              Subscription {status === 'cancelled' ? 'Ended' : 'Expired'}
            </h3>
            <p className="text-sm text-neutral-400 mt-1">
              Your {plan} subscription has ended. You've been moved to the Free plan. Upgrade anytime to restore premium features.
            </p>
          </div>
        </div>
      </motion.div>
    );
  }
  
  return null;
};

const Dashboard = () => {
  useSEO({ title: 'Dashboard — EpisodeKit', path: '/dashboard', index: false });
  const { user, updateUser } = useAuth();
  const location = useLocation();
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [lastFetchInfo, setLastFetchInfo] = useState(null); // Track fetch details
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [planFeatures, setPlanFeatures] = useState({});
  const [tonePresets, setTonePresets] = useState(['professional']);
  const [promoReelEnabled, setPromoReelEnabled] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    file: null,
    title: '',
    description: '',
    tone: 'professional',
    advancedInstructions: ''
  });
  
  // Track fetch state
  const fetchCountRef = useRef(0);
  const lastLocationKeyRef = useRef(null);
  const episodesRef = useRef([]);          // mirrors episodes for stable read inside callbacks
  const silentRetryRef = useRef(null);     // pending silent retry timer (poll failure recovery)
  
  // Keep ref in sync so fetchEpisodes can read current count without
  // listing `episodes` in its deps (avoids re-creating the callback on every poll).
  useEffect(() => {
    episodesRef.current = episodes;
  }, [episodes]);
  
  // Cleanup silent retry on unmount
  useEffect(() => {
    return () => {
      if (silentRetryRef.current) {
        clearTimeout(silentRetryRef.current);
        silentRetryRef.current = null;
      }
    };
  }, []);

  // Admin-only: check if Promo Reel feature is enabled in this environment.
  // The button stays hidden until the backend confirms it's safe to use.
  useEffect(() => {
    const isAdminEmail = (user?.email || '').toLowerCase() === 'factfrenzyofcl@gmail.com';
    if (!isAdminEmail) {
      setPromoReelEnabled(false);
      return;
    }
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('token');
    if (!apiUrl || !token) return;
    fetch(`${apiUrl}/api/admin/promo-reel/feature`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : { enabled: false })
      .then(d => setPromoReelEnabled(Boolean(d?.enabled)))
      .catch(() => setPromoReelEnabled(false));
  }, [user?.email]);
  
  // Determine if user has Pro features (use API data or fallback to user plan)
  const isPro = planFeatures?.advanced_instructions || 
                user?.plan === 'pro' || 
                user?.plan === 'unlimited';
  const hasBasicPlus = planFeatures?.tone_presets || 
                       ['basic', 'pro', 'unlimited'].includes(user?.plan);

  const fetchEpisodes = useCallback(async (showLoading = false, source = 'unknown') => {
    const fetchId = ++fetchCountRef.current;
    
    console.log(`[Dashboard] fetchEpisodes #${fetchId} START - source=${source}, user=${user?.id}, showLoading=${showLoading}`);
    
    if (showLoading) {
      setLoading(true);
      // Clear stale episodes on explicit refresh to avoid showing old data
      // But don't clear on background poll
    }
    setFetchError(null);
    
    try {
      const startTime = Date.now();
      console.log(`[Dashboard] #${fetchId} Calling API...`);
      
      const response = await episodesAPI.getAll();
      const duration = Date.now() - startTime;
      const count = response.data?.length || 0;
      
      console.log(`[Dashboard] #${fetchId} Response: status=${response.status}, count=${count}, duration=${duration}ms`);
      
      // Log each episode for debugging
      if (response.data) {
        response.data.forEach((ep, i) => {
          console.log(`[Dashboard] #${fetchId} Episode[${i}]: id=${ep.id?.slice(0,8)}, status=${ep.status}, title="${ep.title?.slice(0,30)}"`);
        });
      }
      
      // Update state
      setEpisodes(response.data || []);
      setLastFetchInfo({
        fetchId,
        source,
        status: response.status,
        count,
        duration,
        timestamp: new Date().toISOString()
      });
      
      // Warn if returned unexpectedly empty
      if (count === 0 && user?.episodes_used > 0) {
        console.warn(`[Dashboard] #${fetchId} WARNING: API returned 0 episodes but user.episodes_used=${user.episodes_used}`);
      }
      
    } catch (error) {
      console.error(`[Dashboard] #${fetchId} ERROR:`, error);
      console.error(`[Dashboard] #${fetchId} Error details: status=${error.response?.status}, detail=${error.response?.data?.detail}`);
      
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to load episodes';
      
      // Determine if this is a transient/background failure where we should
      // keep the current UI rather than flashing an error screen.
      // - Background polls should never replace the UI with an error
      // - First-load failures (no episodes yet) should still surface the error
      const isBackgroundPoll = !showLoading;
      const haveExistingData = episodesRef.current.length > 0;
      const keepUiStable = isBackgroundPoll || haveExistingData;
      
      if (!keepUiStable) {
        setFetchError(errorMsg);
      }
      
      setLastFetchInfo({
        fetchId,
        source,
        status: error.response?.status || 'network_error',
        error: errorMsg,
        timestamp: new Date().toISOString()
      });
      
      // Show toast only on explicit user action AND when we have no data
      if (showLoading && !haveExistingData) {
        toast.error(`Failed to load episodes: ${errorMsg}`);
      }
      
      // Silent retry once after 2.5s on transient background failures.
      // The regular poll interval will continue regardless.
      if (keepUiStable && !silentRetryRef.current) {
        silentRetryRef.current = setTimeout(() => {
          silentRetryRef.current = null;
          fetchEpisodes(false, 'silent-retry');
        }, 2500);
      }
      
      // DON'T clear episodes on error - keep showing what we have
    } finally {
      setLoading(false);
      console.log(`[Dashboard] #${fetchId} END`);
    }
  }, [user?.id, user?.episodes_used]);

  // Fetch episodes on mount AND when navigating back to this page
  useEffect(() => {
    const currentKey = location.key || 'initial';
    const isNewNavigation = currentKey !== lastLocationKeyRef.current;
    
    console.log(`[Dashboard] useEffect - key=${currentKey}, prevKey=${lastLocationKeyRef.current}, isNew=${isNewNavigation}`);
    lastLocationKeyRef.current = currentKey;
    
    // Always fetch fresh data on navigation (including back button)
    fetchEpisodes(true, isNewNavigation ? 'navigation' : 'mount');
    
    // Fetch plan features and tone presets
    const fetchPlanConfig = async () => {
      try {
        const [featuresRes, tonesRes] = await Promise.all([
          configAPI.getPlanFeatures(),
          configAPI.getTonePresets()
        ]);
        
        // Extract features from response
        const features = featuresRes.data?.features || {};
        const availableTones = featuresRes.data?.available_tones || [];
        const presetIds = tonesRes.data?.presets?.map(p => p.id) || [];
        
        console.log('[Dashboard] Plan features loaded:', features);
        console.log('[Dashboard] Available tones:', availableTones);
        
        setPlanFeatures(features);
        
        // Use available_tones from plan features, or fallback to presets
        const tones = availableTones.length > 0 ? availableTones : 
                      presetIds.length > 0 ? presetIds : ['professional'];
        setTonePresets(tones);
      } catch (err) {
        console.error('Failed to fetch plan config:', err);
        // Fallback: if user is pro, enable features anyway
        if (user?.plan === 'pro' || user?.plan === 'unlimited') {
          setPlanFeatures({
            tone_presets: true,
            advanced_instructions: true,
            best_quotes: true,
            seo_titles: true,
            tiktok_captions: true,
            instagram_captions: true,
            zip_export: true,
            regen_limit: 10
          });
          setTonePresets(['neutral', 'professional', 'casual', 'storytelling', 'humorous']);
        }
      }
    };
    fetchPlanConfig();
    
    // Adaptive polling handled in separate effect
  }, [fetchEpisodes, location.key, user?.plan]);

  // Adaptive polling: 3s when processing, 15s when idle
  const pollIntervalRef = useRef(null);
  const hasProcessingRef = useRef(false);
  useEffect(() => {
    const hasProcessing = episodes.some(ep =>
      ['pending', 'uploading', 'transcribing', 'transcription_progress',
       'generating_notes', 'generating_chapters', 'generating_blog', 'generating_promo'].includes(ep.status)
    );
    // Only recreate interval if processing state actually changed
    if (hasProcessing !== hasProcessingRef.current || !pollIntervalRef.current) {
      hasProcessingRef.current = hasProcessing;
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      const ms = hasProcessing ? 3000 : 15000;
      pollIntervalRef.current = setInterval(() => fetchEpisodes(false, 'poll'), ms);
    }
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    };
  }, [episodes, fetchEpisodes]);

  // Manual retry handler
  const handleRetry = useCallback(() => {
    console.log('[Dashboard] Manual retry triggered');
    fetchEpisodes(true, 'manual_retry');
  }, [fetchEpisodes]);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setUploadForm(prev => ({
        ...prev,
        file: acceptedFiles[0],
        title: acceptedFiles[0].name.replace(/\.[^/.]+$/, '')
      }));
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'audio/*': ['.mp3', '.wav', '.m4a', '.ogg', '.flac'],
      'video/*': ['.mp4', '.webm', '.mov']
    },
    maxFiles: 1
  });

  const handleUpload = async () => {
    if (!uploadForm.file) {
      toast.error('Please select a file');
      return;
    }

    if (user.episodes_used >= user.episodes_limit) {
      toast.error('Monthly limit reached. Please upgrade your plan.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const file = uploadForm.file;
    const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks
    const useChunked = file.size > CHUNK_SIZE;

    try {
      if (useChunked) {
        // --- Chunked upload for large files ---
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

        // Step 1: Init session
        const initRes = await episodesAPI.uploadChunkedInit({
          filename: file.name,
          file_size: file.size,
          total_chunks: totalChunks,
          title: uploadForm.title,
          description: uploadForm.description,
          tone: uploadForm.tone,
          advanced_instructions: (uploadForm.advancedInstructions && planFeatures?.advanced_instructions)
            ? uploadForm.advancedInstructions : '',
        });
        const uploadId = initRes.data.upload_id;

        // Step 2: Send each chunk with per-chunk retry
        const MAX_CHUNK_RETRIES = 3;
        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const blob = file.slice(start, end);

          let chunkSuccess = false;
          for (let attempt = 1; attempt <= MAX_CHUNK_RETRIES; attempt++) {
            try {
              const chunkForm = new FormData();
              chunkForm.append('upload_id', uploadId);
              chunkForm.append('chunk_index', i.toString());
              chunkForm.append('chunk', blob, `chunk_${i}`);
              await episodesAPI.uploadChunkedPart(chunkForm);
              chunkSuccess = true;
              break;
            } catch (chunkErr) {
              if (attempt < MAX_CHUNK_RETRIES) {
                await new Promise(r => setTimeout(r, 1000 * attempt)); // backoff
              } else {
                throw new Error(`Chunk ${i + 1}/${totalChunks} failed after ${MAX_CHUNK_RETRIES} attempts. Please try again.`);
              }
            }
          }

          setUploadProgress(Math.round(((i + 1) / totalChunks) * 90));
        }

        // Step 3: Complete
        setUploadProgress(95);
        await episodesAPI.uploadChunkedComplete({ upload_id: uploadId });
        setUploadProgress(100);
      } else {
        // --- Standard upload for small files ---
        const formData = new FormData();
        formData.append('file', file);
        formData.append('title', uploadForm.title);
        formData.append('description', uploadForm.description);
        formData.append('tone', uploadForm.tone);
        if (uploadForm.advancedInstructions && planFeatures?.advanced_instructions) {
          formData.append('advanced_instructions', uploadForm.advancedInstructions);
        }

        await episodesAPI.upload(formData, (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        });
      }

      toast.success('Episode uploaded! Processing started.');
      setShowUploadDialog(false);
      setUploadForm({ file: null, title: '', description: '', tone: 'professional', advancedInstructions: '' });
      updateUser({ episodes_used: user.episodes_used + 1 });
      fetchEpisodes(true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Upload timed out. Please check your connection and try again.');
      } else if (detail) {
        toast.error(detail);
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error('Upload failed. Please try again.');
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleCreateDemoPdf = async (episode) => {
    const t = toast.loading('Generating demo PDF...');
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${apiUrl}/api/admin/episodes/${episode.id}/demo-pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `EpisodeKit_Demo_${(episode.title || 'episode').replace(/[^\w\s-]/g, '').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Demo PDF downloaded', { id: t });
    } catch (e) {
      toast.error(`Demo PDF failed: ${e.message}`, { id: t });
    }
  };

  const handleResetFastReel = async (episode) => {
    if (!window.confirm(`Reset Fast Reel state for "${episode.title || episode.id}"?\n\nThis clears any stuck/abandoned render and deletes partial output. The next click of the Zap button will start a clean new render.`)) return;
    const token = localStorage.getItem('token');
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    const t = toast.loading('Resetting fast reel state...');
    const ctrl = new AbortController();
    const tid = setTimeout(() => ctrl.abort(), 12000);
    try {
      const r = await fetch(
        `${apiUrl}/api/admin/episodes/${episode.id}/fast-reel/reset`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          signal: ctrl.signal,
        },
      );
      clearTimeout(tid);
      if (!r.ok) {
        // Surface the backend error body instead of a generic FE message.
        let detail = `${r.status} ${r.statusText}`;
        try {
          const body = await r.json();
          detail = body?.detail || body?.error || detail;
        } catch { /* non-json body */ }
        throw new Error(detail);
      }
      const j = await r.json();
      const cleanedCount = (j.disk_cleanup?.disk_cleaned || []).length;
      toast.success(
        `Fast Reel reset. You can generate again. (${cleanedCount} disk artifact${cleanedCount === 1 ? '' : 's'} cleared)`,
        { id: t },
      );
      // Refresh the episode list so the row no longer shows the ghost
      // status — the fast_reel doc was just $unset on the backend.
      if (typeof fetchEpisodes === 'function') {
        await fetchEpisodes();
      }
    } catch (e) {
      clearTimeout(tid);
      const msg = e?.name === 'AbortError'
        ? 'Reset timed out after 12s'
        : (e?.message || 'Reset failed');
      toast.error(`Reset failed: ${msg}`, { id: t });
    }
  };

  const handleCreatePromoReel = async (episode, preset = 'cinematic', opts = {}) => {
    const isFast = preset === 'fast';
    const reelLabel = isFast ? (opts.safeEncoder ? 'fast reel (safe 720)' : 'fast reel') : 'reel';
    const apiBase = isFast ? 'fast-reel' : 'promo-reel';
    const fileLabel = isFast ? 'FastReel' : 'Reel';
    const t = toast.loading(`Starting ${reelLabel} render...`);
    const token = localStorage.getItem('token');
    const apiUrl = process.env.REACT_APP_BACKEND_URL;

    // Per-fetch timeout helper — prevents fetch from piling up if a single
    // request hangs on the proxy. Each poll has its own AbortController so
    // a stuck request can never block the next one.
    const fetchWithTimeout = async (url, opts = {}, timeoutMs = 12000) => {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), timeoutMs);
      try {
        return await fetch(url, { ...opts, signal: ctrl.signal });
      } finally {
        clearTimeout(timer);
      }
    };

    // Probe whether the MP4 is already on disk AND large enough to be valid.
    // A real reel is 1-3 MB; anything below 50KB is a partial encode.
    const probeFileExists = async () => {
      try {
        const r = await fetchWithTimeout(
          `${apiUrl}/api/admin/episodes/${episode.id}/${apiBase}/debug`,
          { headers: { Authorization: `Bearer ${token}` } },
          8000,
        );
        if (!r.ok) return null;
        return await r.json();
      } catch {
        return null;
      }
    };
    const isReelReady = (dbg) =>
      Boolean(dbg?.live_disk_check?.exists) &&
      (dbg?.live_disk_check?.size_bytes || 0) >= 50_000;

    try {
      // Kick off background render
      const generateUrl = isFast && opts.safeEncoder
        ? `${apiUrl}/api/admin/episodes/${episode.id}/${apiBase}/generate?safe_encoder=true`
        : `${apiUrl}/api/admin/episodes/${episode.id}/${apiBase}/generate`;
      const start = await fetchWithTimeout(
        generateUrl,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
        12000,
      );
      if (!start.ok) {
        if (start.status === 503) throw new Error('Promo Reel is temporarily disabled');
        const detail = await start.json().catch(() => ({}));
        throw new Error(detail.detail || `HTTP ${start.status}`);
      }

      // Resilient polling:
      //   • Up to 5 minutes wall clock.
      //   • On any error/non-OK response, exponential backoff (3s → 12s).
      //   • Tolerate up to 60s of consecutive errors before considering it lost.
      //   • When tolerance exceeded, probe the debug/download endpoints first —
      //     the render may have completed despite the connection dropping.
      //   • A 401 stops polling but does NOT log the user out.
      const startedAt = Date.now();
      const overallBudgetMs = 5 * 60_000;
      let last = null;
      let firstErrorAt = 0;
      const ERROR_TOLERANCE_MS = 60_000;
      let backoffMs = 3000;
      const BACKOFF_MAX = 12_000;

      const sleep = (ms) => new Promise(r => setTimeout(r, ms));

      while (Date.now() - startedAt < overallBudgetMs) {
        await sleep(backoffMs);
        let res, errKind = null;
        try {
          res = await fetchWithTimeout(
            `${apiUrl}/api/admin/episodes/${episode.id}/${apiBase}/status`,
            { headers: { Authorization: `Bearer ${token}` } },
            12000,
          );
        } catch (netErr) {
          errKind = 'network';
        }

        if (!errKind && res && res.status === 401) {
          // Auth failure during polling — stop politely, never logout.
          throw new Error('Session check failed during render. Please refresh and check the episode again.');
        }
        if (!errKind && res && !res.ok) errKind = `http_${res.status}`;

        if (errKind) {
          if (firstErrorAt === 0) firstErrorAt = Date.now();
          const errAge = Date.now() - firstErrorAt;
          backoffMs = Math.min(BACKOFF_MAX, Math.round(backoffMs * 1.6));
          toast.loading(
            `Connection unstable (${errKind}, ${Math.round(errAge / 1000)}s) — render still running in background…`,
            { id: t },
          );
          if (errAge > ERROR_TOLERANCE_MS) {
            // Before declaring lost, ask the backend directly via the debug
            // endpoint. The render may have completed.
            const dbg = await probeFileExists();
            if (isReelReady(dbg)) {
              last = { status: 'completed', progress: 100, message: 'Reel ready (recovered after connection drop)' };
              break;
            }
            const reelStatus = dbg?.promo_reel?.status;
            if (reelStatus === 'failed') {
              throw new Error(dbg?.promo_reel?.error || 'Render failed (see admin debug)');
            }
            // Still rendering or status unknown — keep going but note it.
            firstErrorAt = Date.now() - 30_000;  // give it 30s more before re-checking
          }
          continue;
        }

        // Successful poll
        firstErrorAt = 0;
        backoffMs = 3000;
        last = await res.json().catch(() => null);
        if (!last) continue;
        toast.loading(`Rendering reel… ${last.progress || 0}% — ${last.message || ''}`, { id: t });
        if (last.status === 'completed') break;
        if (last.status === 'failed') throw new Error(last.error || 'Reel render failed');

        // Zombie-state short-circuit: if status says "rendering" but the
        // backend updated_at is stale (>90s), the worker died and the
        // watchdog hasn't run yet. Fail fast instead of waiting 5 minutes.
        if (last.status === 'rendering') {
          const upd = last.updated_at;
          if (upd) {
            const ageMs = Date.now() - new Date(upd).getTime();
            if (ageMs > 90_000) {
              const dbg = await probeFileExists();
              if (isReelReady(dbg)) {
                last = { status: 'completed', progress: 100, message: 'Reel recovered from disk' };
                break;
              }
              if (dbg && dbg.is_render_active === false) {
                throw new Error(
                  `Render abandoned: stalled at stage=${dbg.promo_reel?.stage} ` +
                  `progress=${dbg.promo_reel?.progress}% with no heartbeat for ` +
                  `${Math.round(ageMs / 1000)}s. Click Generate again to retry.`
                );
              }
            }
          }
        }
      }

      if (!last || last.status !== 'completed') {
        // One last-chance disk probe before declaring failure
        const dbg = await probeFileExists();
        if (isReelReady(dbg)) {
          last = { status: 'completed' };
        } else {
          throw new Error('Reel did not complete within 5 minutes. The render may still be running — try the debug button.');
        }
      }

      // Trigger download
      toast.loading(`Downloading ${reelLabel}...`, { id: t });
      const dl = await fetchWithTimeout(
        `${apiUrl}/api/admin/episodes/${episode.id}/${apiBase}/download`,
        { headers: { Authorization: `Bearer ${token}` } },
        30000,
      );
      if (!dl.ok) throw new Error(`Download failed (${dl.status})`);
      const blob = await dl.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `EpisodeKit_${fileLabel}_${(episode.title || 'episode').replace(/[^\w\s-]/g, '').replace(/\s+/g, '_')}.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${isFast ? 'Fast reel' : 'Promo reel'} downloaded`, { id: t });
    } catch (e) {
      // On any failure, fetch the debug endpoint and render the actual
      // backend state inline so the admin doesn't have to chase a button.
      let diag = '';
      try {
        const dbg = await probeFileExists();
        if (dbg) {
          const pr = dbg.promo_reel || {};
          const live = dbg.live_disk_check || {};
          diag = [
            `stage: ${pr.stage || pr.status || 'unknown'}`,
            `progress: ${pr.progress ?? '?'}%`,
            pr.error_type ? `type: ${pr.error_type}` : null,
            pr.error ? `error: ${String(pr.error).slice(0, 140)}` : null,
            pr.ffmpeg_exit_code !== undefined && pr.ffmpeg_exit_code !== null ? `ffmpeg_exit: ${pr.ffmpeg_exit_code}` : null,
            pr.render_duration_seconds !== undefined && pr.render_duration_seconds !== null ? `duration: ${pr.render_duration_seconds}s` : null,
            `output_exists: ${live.exists}`,
            live.size_bytes ? `output_size: ${(live.size_bytes / 1024 / 1024).toFixed(2)} MB` : null,
            `is_active: ${dbg.is_render_active}`,
            pr.hostname ? `pod: ${pr.hostname.slice(0, 24)}` : null,
          ].filter(Boolean).join(' · ');
        }
      } catch {}
      const msg = diag
        ? `Reel: ${e.message}\n\n${diag}`
        : `Reel: ${e.message}`;
      toast.error(msg, { id: t, duration: 30000, style: { whiteSpace: 'pre-wrap', maxWidth: 560 } });
      // Also log the full debug to console for copy/paste support
      try {
        const dbg = await probeFileExists();
        if (dbg) console.error('[PromoReel debug]', dbg);
      } catch {}
    }
  };

  const handleDelete = async (episodeId) => {
    if (!window.confirm('Are you sure you want to delete this episode?')) return;

    try {
      await episodesAPI.delete(episodeId);
      toast.success('Episode deleted');
      setEpisodes(prev => prev.filter(ep => ep.id !== episodeId));
    } catch (error) {
      toast.error('Failed to delete episode');
    }
  };

  const handleRetryEpisode = async (episodeId) => {
    try {
      await episodesAPI.retry(episodeId);
      toast.success('Retry started — processing will begin shortly');
      // Optimistically update status to pending
      setEpisodes(prev => prev.map(ep =>
        ep.id === episodeId
          ? { ...ep, status: 'pending', error_message: null, processing_progress: { stage: 'pending', message: 'Retrying...' } }
          : ep
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to retry episode');
    }
  };

  const getStatusBadge = (status, processingProgress, createdAt) => {
    const badges = {
      pending: { class: 'badge-pending', icon: Clock, text: 'Queued', step: 1 },
      uploading: { class: 'badge-processing', icon: Loader2, text: 'Uploading', step: 1 },
      transcribing: { class: 'badge-processing', icon: Loader2, text: 'Transcribing', step: 3 },
      transcription_progress: { class: 'badge-processing', icon: Loader2, text: 'Transcribing', step: 3 },
      generating_notes: { class: 'badge-processing', icon: Loader2, text: 'Generating Notes', step: 4 },
      generating_chapters: { class: 'badge-processing', icon: Loader2, text: 'Creating Chapters', step: 5 },
      generating_blog: { class: 'badge-processing', icon: Loader2, text: 'Writing Blog', step: 6 },
      generating_promo: { class: 'badge-processing', icon: Loader2, text: 'Creating Snippets', step: 7 },
      completed: { class: 'badge-completed', icon: CheckCircle, text: 'Completed', step: 8 },
      failed: { class: 'badge-failed', icon: AlertCircle, text: 'Failed', step: 0 }
    };
    
    // Map processing_progress.stage to better display text
    const stageLabels = {
      'pending': 'Queued',
      'assembling': 'Assembling file...',
      'preparing': 'Preparing audio...',
      'transcribing': null, // use chunk info
      'transcription_complete': 'Transcription complete',
      'generating_notes': 'Generating show notes...',
      'generating_chapters': 'Creating chapters...',
      'generating_blog': 'Writing blog post...',
      'generating_promo': 'Creating snippets...',
      'completed': 'Complete!',
      'failed': 'Failed'
    };
    
    let displayText = badges[status]?.text || status;
    const progressStage = processingProgress?.stage;
    
    // Use staged label or chunk progress
    if (progressStage && stageLabels[progressStage] !== undefined && status !== 'completed' && status !== 'failed') {
      if (stageLabels[progressStage]) {
        displayText = stageLabels[progressStage];
      }
    }
    
    // Show transcription chunk progress
    if (progressStage === 'transcribing' && processingProgress?.transcription_chunks_total > 0) {
      const done = processingProgress.transcription_chunks_done || 0;
      const total = processingProgress.transcription_chunks_total;
      displayText = `Transcribing ${done}/${total}`;
    }

    // Stuck-pending detection: if status is still pending after ~10 min, surface
    // a clearer message so users aren't staring at "Queued" forever. The backend
    // watchdog will recover it within minutes; this is just better UX while it does.
    if (status === 'pending' && createdAt) {
      const ageMin = (Date.now() - new Date(createdAt).getTime()) / 60000;
      if (ageMin > 10) {
        displayText = 'Recovering — this is taking longer than usual';
      }
    }
    // Stuck-transcribing detection: backend has a 30 min hard timeout, so if
    // we ever see ageMin > 35 the watchdog should already be acting. Show a
    // clearer in-progress message instead of just "Transcribing".
    if ((status === 'transcribing' || status === 'transcription_progress') && createdAt) {
      const ageMin = (Date.now() - new Date(createdAt).getTime()) / 60000;
      if (ageMin > 35) {
        displayText = 'Retrying transcription — please hold on';
      }
    }
    if ((status === 'generating_notes' || status === 'generating_chapters' ||
         status === 'generating_blog' || status === 'generating_promo') && createdAt) {
      const ageMin = (Date.now() - new Date(createdAt).getTime()) / 60000;
      if (ageMin > 60) {
        displayText = 'Recovering processing — please hold on';
      }
    }
    
    // Fall back to progress message if available
    if (!displayText && processingProgress?.message && status !== 'completed' && status !== 'failed') {
      displayText = processingProgress.message;
    }
    
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    const isProcessing = ['transcribing', 'transcription_progress', 'generating_notes', 'generating_chapters', 'generating_blog', 'generating_promo', 'uploading', 'pending'].includes(status);
    
    // Calculate overall progress percentage for the progress bar
    const totalSteps = 8;
    const currentStep = badge.step || 1;
    let progressPercent = Math.round((currentStep / totalSteps) * 100);
    
    // Refine with chunk-level granularity during transcription
    if (progressStage === 'transcribing' && processingProgress?.transcription_chunks_total > 0) {
      const chunkFraction = (processingProgress.transcription_chunks_done || 0) / processingProgress.transcription_chunks_total;
      progressPercent = Math.round(((2 + chunkFraction) / totalSteps) * 100);
    }

    return (
      <div className="flex flex-col items-end gap-1">
        <span className={`badge ${badge.class}`} data-testid={`status-badge-${status}`}>
          <Icon className={`w-3 h-3 ${isProcessing ? 'animate-spin' : ''}`} />
          {displayText}
        </span>
        {isProcessing && status !== 'failed' && (
          <div className="w-24 h-1 bg-neutral-800 rounded-full overflow-hidden" data-testid="processing-progress-bar">
            <div 
              className="h-full bg-violet-500 rounded-full transition-all duration-700 ease-out"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        )}
      </div>
    );
  };

  // Task 1: Language selector removed - all content is English only

  return (
    <div className="flex min-h-screen bg-[#050505]" data-testid="dashboard-page">
      <Sidebar />
      
      <main className="flex-1 p-8">
        <div className="max-w-6xl mx-auto">
          {/* Subscription Status Banner */}
          <SubscriptionBanner user={user} />
          
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-unbounded font-bold mb-2">Dashboard</h1>
              <p className="text-neutral-400">
                {user?.episodes_used || 0} of {user?.episodes_limit || 1} episodes used this month
              </p>
              <p className="text-neutral-400 text-sm mt-1">
                Usage: {user?.monthly_minutes_used || 0} / {user?.monthly_minutes_limit || 60} minutes
              </p>
              {/* Show subscription status if not active */}
              {user?.subscription_status && user?.subscription_status !== 'active' && user?.plan !== 'free' && (
                <p className={`text-sm mt-1 ${user?.is_subscription_active ? 'text-blue-400' : 'text-yellow-500'}`}>
                  {user?.subscription_status === 'cancelled' && user?.is_subscription_active
                    ? `Active until ${user?.subscription_end_date ? new Date(user.subscription_end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'period end'}`
                    : `Subscription: ${user.subscription_status}`}
                </p>
              )}
            </div>
            
            <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
              <DialogTrigger asChild>
                <Button 
                  className="btn-primary flex items-center gap-2"
                  disabled={user?.episodes_used >= user?.episodes_limit || user?.payment_failed || user?.subscription_status === 'past_due'}
                  data-testid="upload-episode-btn"
                  title={user?.payment_failed ? 'Update payment method to upload' : ''}
                >
                  <Plus className="w-4 h-4" />
                  Upload Episode
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0A0A0A] border-neutral-800 max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-unbounded">Upload New Episode</DialogTitle>
                </DialogHeader>
                
                <div className="space-y-6 mt-4">
                  {/* Dropzone */}
                  <div
                    {...getRootProps()}
                    className={`dropzone ${isDragActive ? 'active' : ''} ${uploadForm.file ? 'border-success' : ''}`}
                    data-testid="upload-dropzone"
                  >
                    <input {...getInputProps()} />
                    {uploadForm.file ? (
                      <div className="flex items-center gap-3">
                        <FileAudio className="w-8 h-8 text-success" />
                        <div className="text-left">
                          <p className="font-medium">{uploadForm.file.name}</p>
                          <p className="text-sm text-neutral-500">
                            {(uploadForm.file.size / (1024 * 1024)).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <Upload className="w-10 h-10 mx-auto mb-3 text-neutral-500" />
                        <p className="text-neutral-400">Drag & drop or click to upload</p>
                        <p className="text-sm text-neutral-500 mt-1">MP3, WAV, MP4 — No file size limit</p>
                        <p className="text-xs text-neutral-600 mt-1">
                          Up to {planFeatures?.max_episode_minutes || (user?.plan === 'free' ? 60 : 120)} minutes per episode
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Processing Info Microtext */}
                  <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-neutral-900/50 border border-neutral-800">
                    <span className="text-amber-400 text-sm">💡</span>
                    <p className="text-xs text-neutral-500">
                      AI processing may take 1–3 minutes depending on episode length.
                    </p>
                  </div>

                  {/* Title */}
                  <div>
                    <label className="text-sm text-neutral-400 mb-2 block">Episode Title</label>
                    <Input
                      value={uploadForm.title}
                      onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                      placeholder="My Podcast Episode"
                      className="input-base w-full"
                      data-testid="episode-title-input"
                    />
                  </div>

                  {/* Tone Selection */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-neutral-400 mb-2 block flex items-center gap-2">
                        Tone
                        {tonePresets.length > 1 && hasBasicPlus && (
                          <span className="text-xs text-green-500">✓ {tonePresets.length} tones</span>
                        )}
                        {!hasBasicPlus && <Crown className="w-3 h-3 text-yellow-500" />}
                      </label>
                      <Select
                        value={uploadForm.tone}
                        onValueChange={(value) => setUploadForm({ ...uploadForm, tone: value })}
                      >
                        <SelectTrigger className="input-base" data-testid="tone-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#171717] border-neutral-800">
                          {tonePresets.map((tone) => (
                            <SelectItem key={tone} value={tone} className="capitalize">
                              {tone.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Advanced Instructions (Pro+) */}
                  <div>
                    <label className="text-sm text-neutral-400 mb-2 block flex items-center gap-2">
                      Advanced Instructions
                      {!isPro && <Crown className="w-3 h-3 text-yellow-500" />}
                      {!isPro && <span className="text-xs opacity-60">(Pro)</span>}
                    </label>
                    <Textarea
                      value={uploadForm.advancedInstructions}
                      onChange={(e) => setUploadForm({ ...uploadForm, advancedInstructions: e.target.value })}
                      placeholder={isPro 
                        ? "E.g., Focus on actionable tips, use bullet points, mention sponsors..."
                        : "Upgrade to Pro to customize AI generation"}
                      disabled={!isPro}
                      className="input-base w-full min-h-[80px] resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="advanced-instructions-input"
                    />
                  </div>

                  {/* Upload Progress */}
                  {uploading && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Uploading...</span>
                        <span>{uploadProgress}%</span>
                      </div>
                      <div className="progress-bar">
                        <div className="progress-bar-fill" style={{ width: `${uploadProgress}%` }} />
                      </div>
                    </div>
                  )}

                  {/* Submit */}
                  <Button
                    onClick={handleUpload}
                    disabled={!uploadForm.file || uploading}
                    className="btn-primary w-full"
                    data-testid="submit-upload-btn"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      'Start Processing'
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Episodes Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20" data-testid="episodes-loading">
              <div className="spinner" />
              <span className="ml-3 text-neutral-400">
                {episodes.some(ep => ['transcribing','processing','generating','queued'].includes(ep.status))
                  ? 'Processing your episode…'
                  : 'Loading episodes...'}
              </span>
            </div>
          ) : (fetchError && episodes.length === 0) ? (
            <motion.div 
              className="card-base text-center py-16 border-red-800"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="episodes-error"
            >
              <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
              <h3 className="text-xl font-semibold mb-2">Failed to Load Episodes</h3>
              <p className="text-neutral-400 mb-4">{fetchError}</p>
              <Button 
                onClick={handleRetry}
                className="btn-primary"
                data-testid="episodes-retry-btn"
              >
                <Loader2 className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            </motion.div>
          ) : episodes.length === 0 ? (
            <motion.div 
              className="card-base text-center py-16"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="episodes-empty"
            >
              <Mic className="w-16 h-16 mx-auto mb-4 text-neutral-600" />
              <h3 className="text-xl font-semibold mb-2">No Episodes Yet</h3>
              <p className="text-neutral-400 mb-6">Upload your first podcast episode to get started</p>
              <div className="flex flex-col items-center gap-3">
                <Button 
                  onClick={() => setShowUploadDialog(true)}
                  className="btn-primary"
                >
                  Upload Episode
                </Button>
                {/* Show refresh button in case data is stale */}
                <button 
                  onClick={handleRetry}
                  className="text-sm text-neutral-500 hover:text-neutral-300 flex items-center gap-1"
                  data-testid="episodes-refresh-btn"
                >
                  <Loader2 className="w-3 h-3" />
                  Refresh
                </button>
              </div>
              {/* Debug info in dev */}
              {lastFetchInfo && (
                <p className="text-xs text-neutral-600 mt-4">
                  Last fetch: {lastFetchInfo.source} ({lastFetchInfo.count} results)
                </p>
              )}
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {episodes.map((episode, index) => (
                <motion.div
                  key={episode.id}
                  className="card-base"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  data-testid={`episode-card-${episode.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold truncate" title={episode.title}>
                        {episode.title}
                      </h3>
                      <p className="text-xs text-neutral-500 mt-1">
                        {new Date(episode.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    {getStatusBadge(episode.status, episode.processing_progress, episode.created_at)}
                  </div>

                  {episode.duration_minutes && (
                    <p className="text-sm text-neutral-400 mb-3">
                      <Clock className="w-3 h-3 inline mr-1" />
                      {Math.round(episode.duration_minutes)} min
                    </p>
                  )}

                  {episode.error_message && (
                    <p className="text-sm text-red-400 mb-3" data-testid={`episode-error-${episode.id}`}>
                      {episode.error_message.length > 120 
                        ? episode.error_message.substring(0, 120) + '...' 
                        : episode.error_message}
                    </p>
                  )}

                  <div className="flex items-center gap-2 mt-4 pt-4 border-t border-neutral-800">
                    {['pending', 'uploading', 'transcribing', 'transcription_progress', 
                      'generating_notes', 'generating_chapters', 'generating_blog', 'generating_promo'].includes(episode.status) ? (
                      <div className="flex-1 text-center text-xs text-neutral-500 py-2" data-testid={`episode-processing-${episode.id}`}>
                        Processing — please wait
                      </div>
                    ) : episode.status === 'failed' ? (
                      <>
                        <Button
                          variant="ghost"
                          className="btn-ghost flex-1 flex items-center justify-center gap-2 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                          onClick={() => handleRetryEpisode(episode.id)}
                          data-testid={`retry-episode-${episode.id}`}
                        >
                          <RefreshCw className="w-4 h-4" />
                          Retry
                        </Button>
                        <Button
                          variant="ghost"
                          className="btn-ghost text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          onClick={() => handleDelete(episode.id)}
                          data-testid={`delete-episode-${episode.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Link to={`/episode/${episode.id}`} className="flex-1">
                          <Button 
                            variant="ghost" 
                            className="btn-ghost w-full flex items-center justify-center gap-2"
                            disabled={episode.status !== 'completed'}
                            data-testid={`view-episode-${episode.id}`}
                          >
                            <Eye className="w-4 h-4" />
                            View
                          </Button>
                        </Link>
                        {/* Admin-only: Create Demo PDF button */}
                        {(user?.email || '').toLowerCase() === 'factfrenzyofcl@gmail.com' && episode.status === 'completed' && (
                          <Button
                            variant="ghost"
                            className="btn-ghost text-secondary hover:text-secondary/80 hover:bg-secondary/10"
                            onClick={() => handleCreateDemoPdf(episode)}
                            data-testid={`demo-pdf-${episode.id}`}
                            title="Create Demo PDF (admin)"
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                        )}
                        {/* Admin-only: Create Promo Reel button (gated by backend feature flag) */}
                        {(user?.email || '').toLowerCase() === 'factfrenzyofcl@gmail.com' && episode.status === 'completed' && promoReelEnabled && (
                          <Button
                            variant="ghost"
                            className="btn-ghost text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                            onClick={() => handleCreatePromoReel(episode, 'cinematic')}
                            data-testid={`promo-reel-${episode.id}`}
                            title="Generate Promo Reel — full cinematic (admin)"
                          >
                            <Mic className="w-4 h-4" />
                          </Button>
                        )}
                        {/* Admin-only: Create FAST Reel button (22-28s marketing cut) */}
                        {(user?.email || '').toLowerCase() === 'factfrenzyofcl@gmail.com' && episode.status === 'completed' && promoReelEnabled && (
                          <Button
                            variant="ghost"
                            className="btn-ghost text-pink-400 hover:text-pink-300 hover:bg-pink-500/10"
                            onClick={(e) => {
                              if (e.shiftKey || e.altKey) {
                                handleResetFastReel(episode);
                              } else if (e.ctrlKey || e.metaKey) {
                                handleCreatePromoReel(episode, 'fast', { safeEncoder: true });
                              } else {
                                handleCreatePromoReel(episode, 'fast');
                              }
                            }}
                            data-testid={`fast-reel-${episode.id}`}
                            title="Generate Fast Reel — 22-28s marketing cut (admin) · Shift/Alt-click = Reset stuck state · Ctrl/Cmd-click = Safe 720 encoder (production reliability bisect)"
                          >
                            <Zap className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          className="btn-ghost text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          onClick={() => handleDelete(episode.id)}
                          data-testid={`delete-episode-${episode.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
